import { useRef, useEffect } from 'react';
import { useAnimation } from 'framer-motion';
import { useIntersectionObserver, useMediaQuery } from 'usehooks-ts';

import { asIntersectionObserverRef } from '../../../utils/hooks';
import Section from '../../../uiParts/Sections/Section/Section';
import LazyLoadImage from '../../../uiParts/LazyLoadImage/LazyLoadImage';
import FadeIn from '../../../uiParts/FadeIn/FadeIn';
import { theme } from '../../../styles/theme';

import styles from './ProjectDemo.module.css';

type Device = {
  src: string;
  alt: string;
};
interface IProps {
  devices: {
    pc: Device;
    sp?: Device;
  };
}

export default function ProjectDemo({ devices }: IProps) {
  const control = useAnimation();
  const ref = useRef<HTMLElement>(null);
  const entry = useIntersectionObserver(asIntersectionObserverRef(ref), {
    root: null,
    rootMargin: '20px',
    threshold: 0.5,
    freezeOnceVisible: true,
  });
  const isVisible = !!entry?.isIntersecting;
  const matches = useMediaQuery(`(max-width: ${theme.breakpoints[2]})`);

  useEffect(() => {
    control.start('show');
  }, [isVisible, control]);

  return (
    <Section ref={ref} className={`${styles.section} ${styles.deviceSection}`}>
      <div className={styles.deviceImage}>
        <FadeIn yOffset={-50} duration={1} controls={control}>
          <LazyLoadImage
            src={devices.pc.src}
            alt={devices.pc.alt}
            width={604}
            height={401}
            option="blur"
            threshold={0.1}
          />
        </FadeIn>
      </div>
      {devices.sp && (
        <div className={styles.deviceImageSp}>
          <FadeIn
            yOffset={matches ? -50 : 0}
            xOffset={matches ? 0 : -50}
            duration={1}
            controls={control}
            delayOrder={2}
          >
            <LazyLoadImage
              src={devices.sp.src}
              alt={devices.sp.alt}
              width={170}
              height={350}
              option="blur"
              threshold={0.2}
            />
          </FadeIn>
        </div>
      )}
    </Section>
  );
}
